import { useEffect, useLayoutEffect } from 'react';

const DEFAULT_SELECTOR =
  '.stats-container, .stats-container-three, .chart-container, .extra-stats-container, .player-cards-container, .sidebet-wheel-card, .tsunami-meter-card';

export const PageRevealStyles = () => (
  <style>{`
    /* Generic rise + soft bloom for page sections */
    .anim-reveal{ opacity:0; transform: translateY(12px) scale(.985); will-change: transform, opacity, filter; }
    .anim-reveal.is-revealed{
      animation:
        pr-rise 800ms cubic-bezier(.2,.85,.15,1) var(--d,0ms) both,
        pr-bloom 900ms ease-out var(--d,0ms) both;
    }

    @keyframes pr-rise{
      0%   { opacity:0; transform: translateY(12px) scale(.985); }
      60%  { opacity:1; transform: translateY(-2px) scale(1.003); }
      100% { opacity:1; transform: translateY(0)    scale(1); }
    }
    @keyframes pr-bloom{
      0%   { filter: drop-shadow(0 12px 24px rgba(0,0,0,.06)) saturate(.98); }
      100% { filter: drop-shadow(0 8px 16px rgba(0,0,0,.08))  saturate(1); }
    }

    /* Reduce motion: show instantly */
    @media (prefers-reduced-motion: reduce){
      .anim-reveal{ opacity:1 !important; transform:none !important; animation:none !important; filter:none !important; }
    }
  `}</style>
);

export function usePageReveal(rootRef, isActive, selector = DEFAULT_SELECTOR) {
  useLayoutEffect(() => {
    if (!isActive) return;
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(selector);
    targets.forEach((el) => el.classList.add('anim-reveal'));
  }, [isActive, rootRef, selector]);

  useEffect(() => {
    if (!isActive) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const targets = Array.from(root.querySelectorAll('.anim-reveal'));
    if (reduceMotion) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    const inViewNow = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      return r.top < vh * 0.9 && r.bottom > 0 && r.left < vw && r.right > 0;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const idx = targets.indexOf(el);
            if (idx > -1) {
              el.style.setProperty('--d', `${Math.min(idx * 70, 420)}ms`);
            }
            el.classList.add('is-revealed');
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' },
    );

    targets.forEach((el, idx) => {
      if (inViewNow(el)) {
        requestAnimationFrame(() => {
          el.style.setProperty('--d', `${Math.min(idx * 70, 420)}ms`);
          el.classList.add('is-revealed');
        });
      } else {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [isActive, rootRef, selector]);
}

export default PageRevealStyles;
